import { useState, useRef } from "react";
import { GripVertical, Trash2, Image, Upload, Link, Loader2, X, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { ImageBlock } from "@/types/helpArticle";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
}

interface MobileReorderProps {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

interface ImageBlockEditorProps {
  block: ImageBlock;
  onChange: (block: ImageBlock) => void;
  onDelete: () => void;
  dragHandleProps?: DragHandleProps;
  mobileReorderProps?: MobileReorderProps;
}

export function ImageBlockEditor({
  block,
  onChange,
  onDelete,
  dragHandleProps,
  mobileReorderProps,
}: ImageBlockEditorProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [altError, setAltError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUrlChange = (url: string) => {
    setUrlError(null);
    onChange({ ...block, url });
  };

  const handleAltChange = (alt: string) => {
    setAltError(null);
    onChange({ ...block, alt });
  };

  const handleCaptionChange = (caption: string) => {
    onChange({ ...block, caption });
  };

  const handleRemoveImage = () => {
    onChange({ ...block, url: "", alt: "" });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX (actual upload doesn't give progress)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      // Generate unique filename
      const fileExt = file.name.split(".").pop();
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("help-images")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      clearInterval(progressInterval);

      if (uploadError) {
        throw uploadError;
      }

      setUploadProgress(100);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("help-images")
        .getPublicUrl(filePath);

      // Auto-fill alt text with filename (user can edit)
      const autoAlt = block.alt || fileNameWithoutExt.replace(/[-_]/g, " ");

      onChange({ ...block, url: urlData.publicUrl, alt: autoAlt });
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const validateOnBlur = () => {
    if (!block.alt.trim()) {
      setAltError("Alt text is required for accessibility");
    }
  };

  const isValidImageUrl = block.url && (
    block.url.startsWith("http://") || 
    block.url.startsWith("https://") ||
    block.url.startsWith("data:image/")
  );

  return (
    <div className="group flex gap-2 sm:gap-3 p-3 sm:p-4 border rounded-lg bg-card hover:border-primary/50 transition-colors">
      {/* Drag Handle - hidden on mobile */}
      <div
        className="hidden sm:flex items-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-3 sm:space-y-4 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Image className="h-4 w-4 shrink-0" />
            Image
          </Label>
          <div className="flex items-center gap-1">
            {/* Mobile reorder buttons */}
            <div className="flex sm:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveUp}
                disabled={mobileReorderProps?.isFirst}
                className="h-9 w-9"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveDown}
                disabled={mobileReorderProps?.isLast}
                className="h-9 w-9"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-9 w-9 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Image Source Tabs */}
        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="h-4 w-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="url" className="gap-2">
              <Link className="h-4 w-4" />
              URL
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-3 mt-3">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.webp"
                onChange={handleFileUpload}
                className="hidden"
                id={`image-upload-${block.id}`}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Choose Image
                  </>
                )}
              </Button>
              {isUploading && (
                <div className="mt-2">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-1 text-center">
                    {uploadProgress}% uploaded
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                Max 5MB. JPG, PNG, GIF, WebP supported.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="url" className="space-y-3 mt-3">
            <div className="space-y-1.5">
              <Label htmlFor={`image-url-${block.id}`} className="text-xs text-muted-foreground">
                Image URL
              </Label>
              <Input
                id={`image-url-${block.id}`}
                value={block.url}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              {urlError && (
                <p className="text-xs text-destructive">{urlError}</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Alt Text */}
        <div className="space-y-1.5">
          <Label htmlFor={`image-alt-${block.id}`} className="text-xs text-muted-foreground">
            Alt Text <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`image-alt-${block.id}`}
            value={block.alt}
            onChange={(e) => handleAltChange(e.target.value)}
            onBlur={validateOnBlur}
            placeholder="Describe the image for accessibility..."
          />
          {altError && (
            <p className="text-xs text-destructive">{altError}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Required for screen readers and accessibility.
          </p>
        </div>

        {/* Caption */}
        <div className="space-y-1.5">
          <Label htmlFor={`image-caption-${block.id}`} className="text-xs text-muted-foreground">
            Caption (optional)
          </Label>
          <Input
            id={`image-caption-${block.id}`}
            value={block.caption || ""}
            onChange={(e) => handleCaptionChange(e.target.value)}
            placeholder="Add a caption to display below the image..."
          />
        </div>

        {/* Image Preview */}
        {isValidImageUrl && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Preview</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveImage}
                className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <X className="h-3 w-3 mr-1" />
                Remove
              </Button>
            </div>
            <div className="border rounded-lg overflow-hidden bg-muted/30 relative">
              <img
                src={block.url}
                alt={block.alt || "Preview"}
                className="max-h-48 w-auto mx-auto object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  setUrlError("Failed to load image. Please check the URL.");
                }}
                onLoad={() => setUrlError(null)}
              />
              {block.caption && (
                <p className="text-sm text-muted-foreground text-center py-2 px-4 border-t">
                  {block.caption}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
